import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useMemo, useRef } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router";

import { updateWatchedFieldsUtils } from "src/app/features/ApplicationProfile/CardProfileUtils";
import { useCardProfileSaveMutation } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/CardProfileApi";
import type { CardProfileQueryParams } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/services/server/types";
import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import { useCardTypeLoadQuery } from "src/app/features/CardType/services/server/CardTypeApi";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import type { ID } from "../../../../../../../core/types/fe";
import { isTheSameByDeepCompare } from "../../../../../../../core/utils/utilFunctions";
import type { CardProfileEntity } from "../../types/CardProfileEntity";
import { CardProfileFormConfigMap } from "./CardProfileFormConfig";

// Utility to strip only dialogProfileId-dependent *CheckReadOnly fields from form data
const DIALOG_PROFILE_CHECK_READONLY_FIELDS = [
    "dialogsYesCheckReadOnly",
    "dialogsNoCheckReadOnly",
    "dialogsMandatoryCheckReadOnly",
    "dialogsCheckCheckReadOnly",
    "dialogVehicleIdFormatCheckReadOnly",
];
function stripDialogProfileCheckReadOnlyFields(data: CardProfileEntity): CardProfileEntity {
    const result: Partial<CardProfileEntity> = {};
    for (const key in data) {
        if (!DIALOG_PROFILE_CHECK_READONLY_FIELDS.includes(key)) {
            // @ts-expect-error: Index signature
            result[key] = data[key];
        }
    }
    return result as CardProfileEntity;
}

export const CardProfileCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { appProfileId, id } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type") as CardProfileType;
    const location = useLocation();
    const isApac = location.pathname.includes("/apac");

    const resourceUrl = `${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION}`;
    const resourcePath = isApac
        ? `${ResourceName.APPLICATION_PROFILE_LISTS}/${ResourceVariant.APAC}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_APAC}`
        : `${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}`;

    const formNameConfigs = isApac ? CardProfileFormConfigMap.forms.apac : CardProfileFormConfigMap.forms.eft4f;
    const formDefinitionString = formNameConfigs[type];

    // set save params
    useEffect(() => {
        setQueryConfig({ appProfileId });
    }, [appProfileId, resourceUrl]);

    // load form definition
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery(formDefinitionString as string);

    const BASIC_PARAMS = 0;
    const cardTypeIdConfig = useMemo(
        () => formDefinition?.formConfiguration?.tabs[BASIC_PARAMS].fields?.find((item) => item.name === "cardTypeId"),
        [formDefinition],
    );
    const updateFields = cardTypeIdConfig?.updateFields;

    // manage form data
    const [formData, setFormData] = useState<CardProfileEntity>({} as CardProfileEntity);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const prevProfileIdRef = useRef<ID | undefined>(undefined);
    const prevDialogProfileIdRef = useRef<ID | undefined>(undefined);
    const formDataRef = useRef(formData);

    // manage submit action
    const [saveFormData, { isLoading: saveFormIsInProgress }] = useCardProfileSaveMutation();
    const [queryConfig, setQueryConfig] = useState<CardProfileQueryParams>({});

    const onChangeCallback = useCallback(
        (newData: CardProfileEntity) => {
            setFormData({
                ...newData,
                cardCircuitCheckReadOnly: formData.cardCircuitCheckReadOnly,
                posGroupingIdCheckReadOnly: formData.posGroupingIdCheckReadOnly,
                panRangeCheckReadOnly: formData.panRangeCheckReadOnly,
                matchPatternCheckReadOnly: formData.matchPatternCheckReadOnly,
                panLengthMinCheckReadOnly: formData.panLengthMinCheckReadOnly,
                panLengthMaxCheckReadOnly: formData.panLengthMaxCheckReadOnly,
                aidRangeCheckReadOnly: formData.aidRangeCheckReadOnly,
                interchangeDesignatorCheckReadOnly: formData.interchangeDesignatorCheckReadOnly,
                secondByteOfServiceCheckReadOnly: formData.secondByteOfServiceCheckReadOnly,
                thirdByteOfServiceCheckReadOnly: formData.thirdByteOfServiceCheckReadOnly,
                dialogueIndicatorCheckReadOnly: formData.dialogueIndicatorCheckReadOnly,
                releaseCheckReadOnly: formData.releaseCheckReadOnly,
                productCodesCheckReadOnly: formData.productCodesCheckReadOnly,
                mobileNumberMaskCheckReadOnly: formData.mobileNumberMaskCheckReadOnly,
                authCardTypeIdCheckReadOnly: formData.authCardTypeIdCheckReadOnly,
            });
        },
        [formData],
    );

    // update watchedFields values
    const { data: cardTypeEntity } = useCardTypeLoadQuery({ entityId: `${formData.cardTypeId}` }, { skip: !formData.cardTypeId });

    useEffect(() => {
        // Ensure dialogVehicleIdFormat is always set to 'UNDEFINED' if not set
        if (!formData.dialogVehicleIdFormat) {
            setFormData((prev) => ({
                ...prev,
                dialogVehicleIdFormat: "UNDEFINED",
            }));
        }
    }, [formData.dialogVehicleIdFormat]);

    useEffect(() => {
        const currentDialogProfileId = formData?.dialogProfileId;
        const prevDialogProfileId = prevDialogProfileIdRef.current;
        if (currentDialogProfileId !== prevDialogProfileId) {
            prevDialogProfileIdRef.current = currentDialogProfileId;
            const isEmpty = currentDialogProfileId === null || currentDialogProfileId === undefined;
            setFormData((prev) => {
                if (!isEmpty) {
                    // dialogProfileId is set: clear dependent fields and set readonly, but set dialogVehicleIdFormat to 'UNDEFINED'
                    return {
                        ...prev,
                        dialogsYes: undefined,
                        dialogsNo: undefined,
                        dialogsMandatory: undefined,
                        dialogsCheck: undefined,
                        dialogVehicleIdFormat: "UNDEFINED",
                        dialogsYesCheckReadOnly: true,
                        dialogsNoCheckReadOnly: true,
                        dialogsMandatoryCheckReadOnly: true,
                        dialogsCheckCheckReadOnly: true,
                        dialogVehicleIdFormatCheckReadOnly: true,
                    };
                } else {
                    // dialogProfileId is empty: remove readonly, keep values as-is
                    return {
                        ...prev,
                        dialogsYesCheckReadOnly: false,
                        dialogsNoCheckReadOnly: false,
                        dialogsMandatoryCheckReadOnly: false,
                        dialogsCheckCheckReadOnly: false,
                        dialogVehicleIdFormatCheckReadOnly: false,
                    };
                }
            });
        }
    }, [formData.dialogProfileId]);

    const updateWatchedFields = useCallback(() => {
        const currentProfileId = formData?.cardTypeId;

        if (!currentProfileId || !cardTypeEntity || !updateFields) return null;

        prevProfileIdRef.current = currentProfileId;

        if (!cardTypeEntity || !formDataRef.current) return null;

        return updateWatchedFieldsUtils(cardTypeEntity, formDataRef.current, updateFields);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardTypeEntity, updateFields]);

    useEffect(() => {
        const currentProfileId = formData?.cardTypeId;

        if (!cardTypeEntity || !prevProfileIdRef || !currentProfileId) {
            return;
        }

        const prevProfileId = prevProfileIdRef.current;

        if (currentProfileId !== prevProfileId) {
            let newFormData = updateWatchedFields();
            if (newFormData && !isTheSameByDeepCompare(formDataRef.current, newFormData)) {
                setFormData(newFormData);
                formDataRef.current = newFormData;
                newFormData = null;
            }
        }
    }, [cardTypeEntity, formData, updateWatchedFields]);

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const sanitizedFormData = stripDialogProfileCheckReadOnlyFields(formData);
            const result = await saveFormData({ cardProfilesFormData: sanitizedFormData, cardProfileQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "Card Profile",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${resourcePath}/${FORM_ACTION.EDIT}/${result.data.id}?type=${type}`);
            }
        },
        [formData, saveFormData, queryConfig, dispatch, navigate, resourcePath, type],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<CardProfileEntity>
                    name={formDefinition.formConfiguration.resource}
                    formData={type ? { ...formData, id, type } : { ...formData, id }}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
